import { useCallback, useEffect } from 'react';
import { css } from '@emotion/react';
import { TaskStatus } from '@weave/schema-gen-ts/dist/schemas/task-center/shared/v1/enums.pb';
import { CallIntelligenceOrgWetags } from '@wetags/call-intelligence';
import { CallIntelMutations, CallIntelTypes } from '@frontend/api-call-intel';
import { getUser } from '@frontend/auth-helpers';
import { DemoIdUtils } from '@frontend/demo-interceptors';
import { TasksDisabledState } from '@frontend/empty-states#svg';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { TaskEvents, useTaskCitation } from '@frontend/task-center-scope';
import { theme } from '@frontend/theme';
import {
  emptyStateGraphics,
  NakedUl,
  SkeletonLoaders,
  Text,
  TextLink,
  useAlert,
  styles as designSystemStyles,
} from '@frontend/design-system';
import { TASK_ARCHIVE_AGE_IN_DAYS, TASK_DELETE_AGE_IN_MONTHS } from '../constants';
import { useCallIntelDefaultsShallowStore } from '../hooks/use-call-intel-defaults';
import { CallFlagsData } from '../types';
import { callStatusUtils } from '../utils/call-intel-utils';
import { getTaskArchivalPeriods } from '../utils/task-archival-util';
import { generateTrackingId } from '../utils/tracking';
import { CallAnalyzeStatus } from './call-analyze-status';
import { CallIntelChip } from './chip/call-intel-chip';
import { TaskStatusSwitcher } from './input/task-status-switcher';
import { PrivateFeature } from './private-feature';

// Type definitions

type BaseTaskProps = {
  isDemoAccount?: boolean;
  onHighlightTask?: (taskId: string) => void;
  tasks: CallIntelTypes.Task[];
  isTasksEnabled?: boolean;
};

export type CallTasksProps = BaseTaskProps & {
  call: CallIntelTypes.CallRecord;
  callParticipantsMetadata: CallIntelTypes.CallParticipantsMetadata;
  isLoading?: boolean;
  onUpdateTasks?: (call: CallIntelTypes.CallRecord, tasks: CallIntelTypes.Task[]) => void;
  onDeleteTask?: (call: CallIntelTypes.CallRecord, taskId: string) => void;
};

type CallTaskProps = BaseTaskProps & {
  openTaskPanel: (taskId: string, isDemoAccount?: boolean) => void;
  onStatusChange: (taskId: string, newStatus: TaskStatus) => void;
};

type TaskLoaderProps = { count: number };

type EmptyTaskVariant = 'default' | 'archival-possible' | 'deleted';

type EmptyTaskProps = CallFlagsData & {
  variant?: EmptyTaskVariant;
};

type GetEmptyTaskVariantParams = {
  callStartTime?: string;
  isDemoAccount?: boolean;
  isTaskArchivalEnabled: boolean;
};

const getEmptyTaskVariant = ({
  callStartTime,
  isDemoAccount,
  isTaskArchivalEnabled,
}: GetEmptyTaskVariantParams): EmptyTaskVariant => {
  if (!isTaskArchivalEnabled || isDemoAccount) {
    return 'default';
  }

  const { isAllDeleted } = getTaskArchivalPeriods({
    startDate: callStartTime,
    endDate: callStartTime,
  });

  return isAllDeleted ? 'deleted' : 'archival-possible';
};

const TaskLoader = ({ count }: TaskLoaderProps) => {
  const loadersToShow = Math.min(count, 3);
  return (
    <>
      {Array.from({ length: loadersToShow }).map((_, idx) => (
        <div
          key={idx}
          css={css`
            padding: ${theme.spacing(1.5, 2)};
          `}
        >
          <SkeletonLoaders.Loader height={theme.spacing(3.5)} />
          <div
            css={css`
              margin-top: ${theme.spacing(1)};
              display: flex;
              gap: ${theme.spacing(1)};
            `}
          >
            <SkeletonLoaders.Loader height={theme.spacing(3)} width={theme.spacing(12)} />
            <SkeletonLoaders.Loader height={theme.spacing(3)} width={theme.spacing(12)} />
          </div>
        </div>
      ))}
    </>
  );
};

const EmptyTask = ({ isFailedCall, isPoorAudio, isSkippedCall, variant = 'default' }: EmptyTaskProps) => {
  const { t } = useTranslation('call-intel-core');
  const EmptyStateGraphicNoTasks = emptyStateGraphics.noCallFollowUps;

  const renderEmptyTaskMessage = () => {
    switch (variant) {
      case 'archival-possible':
        return (
          <>
            <Text css={styles.emptyStateText} textAlign='center'>
              {t('No Tasks for this call')}
            </Text>

            <Text css={styles.emptyStateText} size='small' textAlign='center'>
              {t('A task may be archived in the Task Center if it is inactive for {{count}} days and past due.', {
                count: TASK_ARCHIVE_AGE_IN_DAYS,
              })}
            </Text>
          </>
        );

      case 'deleted':
        return (
          <Text css={styles.emptyStateText} textAlign='center'>
            {t('Archived tasks are automatically deleted after {{count}} months.', {
              count: TASK_DELETE_AGE_IN_MONTHS,
            })}
          </Text>
        );

      case 'default':
      default:
        return (
          <Text css={styles.emptyStateText} textAlign='center'>
            {t('No Tasks for this call')}
          </Text>
        );
    }
  };

  return (
    <div css={styles.emptyStateWrapper}>
      <div
        css={css`
          width: 100%;
        `}
      >
        <CallAnalyzeStatus
          isFailedCall={isFailedCall}
          isPoorAudio={isPoorAudio}
          isSkippedCall={isSkippedCall}
          type='task'
        />
      </div>
      <EmptyStateGraphicNoTasks height={200} width={200} />
      {renderEmptyTaskMessage()}
    </div>
  );
};

const CallTask = ({ isDemoAccount, openTaskPanel, onStatusChange, onHighlightTask, tasks }: CallTaskProps) => {
  const { chipVariants, dataLabels } = useCallIntelDefaultsShallowStore('chipVariants', 'dataLabels');

  return (
    <NakedUl css={styles.list}>
      {tasks.map((task) => (
        <li
          data-trackingid={generateTrackingId({ customName: 'task-card-open' })}
          key={task.id}
          onClick={() => task.id && openTaskPanel(isDemoAccount ? `demo-${task.id}` : task.id, !!isDemoAccount)}
        >
          <div css={styles.taskTextWrapper}>
            <Text
              css={[
                styles.taskText,
                designSystemStyles.truncate,
                task.status === TaskStatus.STATUS_COMPLETED && styles.strike,
              ]}
            >
              {task.title}
            </Text>
            {task.citationTime && (
              <span css={styles.citationTime}>
                (
                <TextLink
                  data-testid='citation-time-link'
                  onClick={(e) => {
                    e.stopPropagation();
                    task.id && onHighlightTask?.(task.id);
                  }}
                  trackingId={generateTrackingId({
                    component: 'task-card',
                    context: 'timestamp-click',
                    isDemoAccount,
                  })}
                >
                  {task.citationTime}
                </TextLink>
                )
              </span>
            )}
            <Icon name='expand-small' css={styles.icon} />
          </div>
          <div css={styles.taskActionWrapper}>
            <TaskStatusSwitcher
              defaultOptionValue={task.status}
              onStatusChange={(newStatus) => {
                task.id && onStatusChange(task.id, newStatus);
              }}
              trackingId={generateTrackingId({ customName: 'task-card-status-switcher' })}
              value={task.status}
            />

            <CallIntelChip
              as='category'
              chipVariants={chipVariants}
              dataKey={task.type}
              label={dataLabels.taskTypes?.[task.type] || task.type}
              key={task.type}
            />
          </div>
        </li>
      ))}
    </NakedUl>
  );
};

export const CallTasks = ({
  call,
  isDemoAccount,
  isLoading,
  tasks,
  onUpdateTasks,
  onDeleteTask,
  onHighlightTask,
  isTasksEnabled,
}: CallTasksProps) => {
  const { t } = useTranslation('call-intel-core');
  const alert = useAlert();
  const [isCallIntelTaskArchivalEnabled] = CallIntelligenceOrgWetags.useWetag('call-intel-tasks-archival');
  const { openTaskWithTaskId, onTaskPanelEvent, offTaskPanelEvent } = useTaskCitation();

  const user = getUser();

  const callFlags = callStatusUtils.getCallFlags(call);

  const { mutate: updateTask } = CallIntelMutations.useUpdateTask({
    onSuccess: (_: any, taskPayload) => {
      if (taskPayload.status === undefined) return;

      const newStatus: TaskStatus = taskPayload.status;

      const updatedTasks: CallIntelTypes.Task[] = tasks.map((task) =>
        task.id === taskPayload.id ? { ...task, status: newStatus } : task
      );

      onUpdateTasks?.(call, updatedTasks);
    },
    onError: () => {
      alert.error(t('Unable to update task. Please try again.'));
    },
    isDemoAccount,
  });

  const handleStatusChange = useCallback(
    (taskId: string, newStatus: TaskStatus) => {
      if (taskId && user?.userID) {
        updateTask({ id: taskId, status: newStatus, updatedBy: user.userID });
      }
    },
    [updateTask, user?.userID]
  );

  const handleTaskPanelTaskUpdated = useCallback(
    ({ id: taskId, status }: TaskEvents['TASK_UPDATED']) => {
      const updatedTasks: CallIntelTypes.Task[] = tasks.map((task) => {
        if (DemoIdUtils.checkIsDemoId(taskId)) {
          taskId = DemoIdUtils.removeDemoDataFromId(taskId);
        }
        return task.id === taskId ? { ...task, status } : task;
      });
      onUpdateTasks?.(call, updatedTasks);
    },
    [tasks, onUpdateTasks, call]
  );

  const handleTaskPanelTaskDeleted = useCallback(
    ({ id: taskId }: TaskEvents['TASK_DELETED']) => {
      onDeleteTask?.(call, taskId);
    },
    [onDeleteTask, call]
  );

  useEffect(() => {
    onTaskPanelEvent('TASK_UPDATED', handleTaskPanelTaskUpdated);
    onTaskPanelEvent('TASK_DELETED', handleTaskPanelTaskDeleted);

    return () => {
      offTaskPanelEvent('TASK_UPDATED', handleTaskPanelTaskUpdated);
      offTaskPanelEvent('TASK_DELETED', handleTaskPanelTaskDeleted);
    };
  }, [handleTaskPanelTaskUpdated, handleTaskPanelTaskDeleted, onTaskPanelEvent, offTaskPanelEvent]);

  if (callFlags.isPrivateCall) {
    return (
      <div css={styles.emptyStateWrapper}>
        <PrivateFeature />
      </div>
    );
  }

  if (!isTasksEnabled) return <TasksDisabledInfo />;

  if (isLoading) return <TaskLoader count={call.taskCount || 1} />;

  if (tasks?.length === 0) {
    const emptyTaskVariant = getEmptyTaskVariant({
      callStartTime: call.startTime,
      isDemoAccount,
      isTaskArchivalEnabled: isCallIntelTaskArchivalEnabled,
    });

    return <EmptyTask {...callFlags} variant={emptyTaskVariant} />;
  }

  return (
    <CallTask
      isDemoAccount={isDemoAccount}
      onHighlightTask={onHighlightTask}
      onStatusChange={handleStatusChange}
      openTaskPanel={openTaskWithTaskId}
      tasks={tasks}
    />
  );
};

const TasksDisabledInfo = () => {
  const { t } = useTranslation('call-intel-core');
  return (
    <div css={[styles.emptyStateWrapper, { gap: theme.spacing(3) }]}>
      <TasksDisabledState height={250} width={250} />
      <Text css={[styles.emptyStateText, { maxWidth: 320 }]} textAlign='center'>
        {t('Call Intelligence Tasks are disabled for this location.')}
      </Text>
    </div>
  );
};

const styles = {
  citationTime: css`
    margin-right: ${theme.spacing(1)};
  `,

  emptyStateText: css`
    color: var(--fabric-color-text-muted);
  `,

  emptyStateWrapper: css`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: ${theme.spacing(2, 2)};
  `,

  icon: css`
    cursor: pointer;
  `,

  list: css`
    li {
      padding: ${theme.spacing(1.5, 2)};
    }

    li:not(:last-child) {
      border-bottom: 1px solid ${theme.colors.neutral20};
    }

    li:hover {
      background-color: ${theme.colors.neutral5};
      cursor: pointer;
    }
  `,

  notificationBadge: css`
    height: ${theme.spacing(2)};
    width: ${theme.spacing(2)};
    padding: ${theme.spacing(0, 0.5)};
    border-radius: ${theme.borderRadius.full};
  `,

  strike: css`
    text-decoration: line-through;
  `,

  taskActionWrapper: css`
    display: flex;
    align-items: center;
    margin-top: ${theme.spacing(1)};
    gap: ${theme.spacing(1)};
  `,

  taskText: css`
    max-width: 332px;
  `,

  taskTextWrapper: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
  `,
};
